<template>
    <BCard class="regulations-review-section mb-1">
        <template #header>
            <div class="regulations-review-section__heading">
                <div>
                    <h5 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):legislations', 'Regulations') }}
                    </h5>
                    <p v-if="showDescription" class="text-less-muted mb-0">
                        {{ translate(
                            'Component(ResearchReviewProcess):legislations-description',
                            'Confirm regulation coverage, claims, evidence and exemptions extracted from the document.',
                        ) }}
                    </p>
                </div>
                <div class="regulations-review-section__actions">
                    <BButton
                        type="button"
                        variant="outline-primary"
                        class="regulations-review-section__add"
                        @click="addRegulation"
                    >
                        <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                        {{ translate('Component(ResearchReviewProcess):add-legislation', 'Add regulation') }}
                    </BButton>
                    <BDropdown
                        right
                        no-caret
                        variant="outline-secondary"
                        toggle-class="btn-icon"
                        class="regulations-review-section__view-menu"
                        :title="translate('Component(ResearchReviewProcess):regulations-view-options', 'Regulation view options')"
                    >
                        <template #button-content>
                            <FeatherIcon icon="MoreVerticalIcon" size="16" />
                            <span class="sr-only">
                                {{ translate('Component(ResearchReviewProcess):regulations-view-options', 'Regulation view options') }}
                            </span>
                        </template>
                        <BDropdownHeader>
                            {{ translate('Component(ResearchReviewProcess):regulations-view', 'View') }}
                        </BDropdownHeader>
                        <BDropdownItem
                            :active="presentationMode === RegulationReviewPresentationMode.DETAILED"
                            @click="setPresentationMode(RegulationReviewPresentationMode.DETAILED)"
                        >
                            <span class="regulations-review-section__view-check">
                                <FeatherIcon
                                    v-if="presentationMode === RegulationReviewPresentationMode.DETAILED"
                                    icon="CheckIcon"
                                    size="14"
                                />
                            </span>
                            {{ translate('Component(ResearchReviewProcess):regulations-view-detailed', 'Detailed') }}
                        </BDropdownItem>
                        <BDropdownItem
                            :active="presentationMode === RegulationReviewPresentationMode.COMPACT"
                            @click="setPresentationMode(RegulationReviewPresentationMode.COMPACT)"
                        >
                            <span class="regulations-review-section__view-check">
                                <FeatherIcon
                                    v-if="presentationMode === RegulationReviewPresentationMode.COMPACT"
                                    icon="CheckIcon"
                                    size="14"
                                />
                            </span>
                            {{ translate('Component(ResearchReviewProcess):regulations-view-compact', 'Compact') }}
                        </BDropdownItem>
                    </BDropdown>
                </div>
                <BSpinner
                    v-if="resolvingRelatedSubstances"
                    small
                    variant="primary"
                    class="regulations-review-section__resolver"
                />
            </div>
        </template>

        <BAlert v-if="relatedSubstancesError" variant="light-warning" show>
            <div class="alert-body">
                {{ translate(
                    'Component(ResearchReviewProcess):related-substances-load-failed',
                    'Related substance information could not be loaded. The review data is still available below.',
                ) }}
            </div>
        </BAlert>

        <BAlert v-if="!viewModel.items.length" variant="light-secondary" show class="mb-0">
            <div class="alert-body">
                {{ translate('Component(ResearchReviewProcess):no-legislations', 'No regulations were extracted from this document.') }}
            </div>
        </BAlert>

        <component
            :is="regulationCardComponent"
            v-for="item in viewModel.items"
            :key="`${presentationMode}-regulation-${item.index}`"
            :number="item.index + 1"
            :regulation="item.regulation"
            :exemptions="item.exemptions"
            :evaluation="item.evaluation"
            :related-substances="relatedSubstances(item.regulation)"
            :unavailable-legislation-keys="unavailableLegislationKeys(item.index)"
            :expanded="isRegulationExpanded(item.index)"
            :claim-editor-mode="resolvedClaimEditorMode"
            :claims-editor-component="claimEditorComponent"
            @toggle="toggleRegulation(item.index)"
            @input="updateRegulation(item.index, $event)"
            @remove="removeRegulation(item.index)"
            @add-exemption="addExemption(item.regulation)"
            @update-exemption="updateExemption($event.sourceIndex, $event.value)"
            @remove-exemption="removeExemption"
        />

        <CollapsibleCard
            v-if="viewModel.unassignedExemptions.length"
            class="mb-0"
            body-class="p-1"
            :default-collapsed="false"
        >
            <template #title>
                <div>
                    <strong>
                        {{ translate('Component(ResearchReviewProcess):unassigned-exemptions', 'Unassigned exemptions') }}
                    </strong>
                    <small class="text-less-muted d-block">
                        {{ translate(
                            'Component(ResearchReviewProcess):unassigned-exemptions-description',
                            'Select a regulation so these exemptions can be reviewed in context.',
                        ) }}
                    </small>
                </div>
            </template>

            <div class="regulations-review-section__unassigned">
                <div
                    v-for="exemption in viewModel.unassignedExemptions"
                    :key="`unassigned-exemption-${exemption.sourceIndex}`"
                    class="regulations-review-section__unassigned-row"
                >
                    <BFormGroup
                        :label="translate('Component(ResearchReviewProcess):legislation', 'Regulation')"
                        class="mb-0"
                    >
                        <BFormSelect
                            :value="null"
                            :options="regulationOptions"
                            @input="assignExemption(exemption.sourceIndex, $event)"
                        />
                    </BFormGroup>
                    <RegulationExemptionRow
                        :value="exemption.value"
                        @input="updateExemption(exemption.sourceIndex, $event)"
                        @remove="removeExemption(exemption.sourceIndex)"
                    />
                </div>
            </div>
        </CollapsibleCard>
    </BCard>
</template>

<script>
import {
    BAlert,
    BButton,
    BCard,
    BDropdown,
    BDropdownHeader,
    BDropdownItem,
    BFormGroup,
    BFormSelect,
    BSpinner,
} from 'bootstrap-vue'
import CollapsibleCard from '@core/components/common/CollapsibleCard.vue'
import RegulationExemptionRow from './RegulationExemptionRow.vue'
import { ClaimEditorMode, resolveClaimEditor } from './claims/claim-editor-registry'
import { regulationReviewCheckerRegistry } from './regulation-review-checker-registry'
import {
    RegulationReviewPresentationMode,
    resolveRegulationReviewCard,
} from './regulation-review-presentation-registry'
import {
    persistRegulationReviewPresentationMode,
    readRegulationReviewPresentationMode,
} from './regulation-review-presentation-preference'
import {
    belongsToRegulation,
    buildRegulationReviewViewModel,
} from './regulation-review-view-model'
import { getRelatedSubstances } from './substance-regulation-projection'

const clone = value => JSON.parse(JSON.stringify(value || []))

export default {
    name: 'RegulationsReviewSection',
    components: {
        BAlert,
        BButton,
        BCard,
        BDropdown,
        BDropdownHeader,
        BDropdownItem,
        BFormGroup,
        BFormSelect,
        BSpinner,
        CollapsibleCard,
        RegulationExemptionRow,
    },
    props: {
        legislations: {
            type: Array,
            default: () => [],
        },
        availableLegislations: {
            type: Array,
            default: () => [],
        },
        exemptions: {
            type: Array,
            default: () => [],
        },
        checkerRegistry: {
            type: Object,
            default: () => regulationReviewCheckerRegistry,
        },
        substanceDeclarationInference: {
            type: Object,
            default: () => ({}),
        },
        relatedSubstancesProjection: {
            type: Object,
            default: () => ({ byId: {}, byKey: {} }),
        },
        resolvingRelatedSubstances: {
            type: Boolean,
            default: false,
        },
        relatedSubstancesError: {
            type: Boolean,
            default: false,
        },
        claimEditor: {
            type: String,
            default: 'structured',
        },
        showDescription: {
            type: Boolean,
            default: true,
        },
        showTabSubstanceCount: {
            type: Boolean,
            default: false,
        },
        showTabAttention: {
            type: Boolean,
            default: false,
        },
        hideEmptyNegativeSubstances: {
            type: Boolean,
            default: false,
        },
        hideSubstancesUntilDeclaration: {
            type: Boolean,
            default: false,
        },
        allowRemoveRegulation: {
            type: Boolean,
            default: false,
        },
        substances: {
            type: Array,
            default: () => [],
        },
        substanceLinks: {
            type: Array,
            default: () => [],
        },
        missingRegulationSubstanceIds: {
            type: Array,
            default: () => [],
        },
        workspaceService: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            RegulationReviewPresentationMode,
            presentationMode: readRegulationReviewPresentationMode(),
            expandedRegulationIndex: 0,
        }
    },
    computed: {
        regulationCardComponent() {
            return resolveRegulationReviewCard(this.presentationMode)
        },
        claimEditorComponent() {
            return resolveClaimEditor(this.resolvedClaimEditorMode)
        },
        resolvedClaimEditorMode() {
            return this.claimEditor === ClaimEditorMode.GUIDED
                ? ClaimEditorMode.GUIDED
                : ClaimEditorMode.STRUCTURED
        },
        viewModel() {
            return buildRegulationReviewViewModel(
                this.legislations,
                this.exemptions,
                this.checkerRegistry,
            )
        },
        regulationOptions() {
            return [{
                value: null,
                text: this.translate('Component(ResearchReviewProcess):select-legislation', 'Select legislation'),
                disabled: true,
            }].concat(this.legislations.map((regulation, index) => ({
                value: index,
                text: regulation.name || regulation.key || `${index + 1}`,
            })))
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        relatedSubstances(regulation) {
            return getRelatedSubstances(this.relatedSubstancesProjection, regulation)
        },
        unavailableLegislationKeys(index) {
            return this.legislations
                .filter((regulation, regulationIndex) => regulationIndex !== index)
                .map(regulation => regulation && regulation.key)
                .filter(Boolean)
        },
        isLegislationKeyAvailable(index, key) {
            return !key || !this.unavailableLegislationKeys(index).includes(key)
        },
        setPresentationMode(mode) {
            this.presentationMode = mode
            persistRegulationReviewPresentationMode(mode)
            if (mode === RegulationReviewPresentationMode.COMPACT
                && !this.legislations[this.expandedRegulationIndex]) {
                this.expandedRegulationIndex = this.legislations.length ? 0 : null
            }
        },
        isRegulationExpanded(index) {
            return this.presentationMode === RegulationReviewPresentationMode.DETAILED
                || this.expandedRegulationIndex === index
        },
        toggleRegulation(index) {
            if (this.presentationMode !== RegulationReviewPresentationMode.COMPACT) {
                return
            }

            this.expandedRegulationIndex = this.expandedRegulationIndex === index ? null : index
        },
        emitState({
            legislations = this.legislations,
            exemptions = this.exemptions,
        }) {
            this.$emit('update', {
                legislations: clone(legislations),
                exemptions: clone(exemptions),
            })
        },
        addRegulation() {
            this.expandedRegulationIndex = this.legislations.length
            this.emitState({
                legislations: this.legislations.concat({
                    name: '',
                    status: null,
                    key: null,
                    evidence: null,
                    claims: [],
                }),
            })
        },
        updateRegulation(index, regulation) {
            const previousRegulation = this.legislations[index]
            const changesNormalizedKey = regulation.key !== previousRegulation.key

            if (changesNormalizedKey && !this.isLegislationKeyAvailable(index, regulation.key)) {
                return
            }

            const legislations = this.legislations.map((item, itemIndex) => (
                itemIndex === index ? regulation : item
            ))

            const exemptions = this.exemptions.map(exemption => {
                if (!belongsToRegulation(exemption, previousRegulation)) {
                    return exemption
                }

                return {
                    ...exemption,
                    legislation: regulation.name || null,
                    legislationKey: regulation.key || null,
                    legislationId: regulation.id || null,
                }
            })

            this.emitState({ legislations, exemptions })
        },
        removeRegulation(index) {
            if (this.expandedRegulationIndex === index) {
                this.expandedRegulationIndex = this.legislations.length > 1
                    ? Math.min(index, this.legislations.length - 2)
                    : null
            } else if (this.expandedRegulationIndex > index) {
                this.expandedRegulationIndex -= 1
            }

            this.emitState({
                legislations: this.legislations.filter((item, itemIndex) => itemIndex !== index),
            })
        },
        addExemption(regulation) {
            this.emitState({
                exemptions: this.exemptions.concat({
                    name: '',
                    legislation: regulation.name || null,
                    legislationKey: regulation.key || null,
                    legislationId: regulation.id || null,
                    evidence: null,
                }),
            })
        },
        updateExemption(index, exemption) {
            this.emitState({
                exemptions: this.exemptions.map((item, itemIndex) => (
                    itemIndex === index ? exemption : item
                )),
            })
        },
        removeExemption(index) {
            this.emitState({
                exemptions: this.exemptions.filter((item, itemIndex) => itemIndex !== index),
            })
        },
        assignExemption(exemptionIndex, regulationIndex) {
            const regulation = this.legislations[regulationIndex]
            if (!regulation) {
                return
            }

            this.updateExemption(exemptionIndex, {
                ...this.exemptions[exemptionIndex],
                legislation: regulation.name || null,
                legislationKey: regulation.key || null,
                legislationId: regulation.id || null,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.regulations-review-section__heading {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.regulations-review-section__add {
    flex: 0 0 auto;
}

.regulations-review-section__actions {
    display: flex;
    flex: 0 0 auto;
    gap: .75rem;
    align-items: center;
    margin-left: auto;
}

.regulations-review-section__view-menu ::v-deep .dropdown-toggle {
    width: 2.75rem;
    height: 100%;
}

.regulations-review-section__view-check {
    display: inline-flex;
    width: 1.25rem;
    align-items: center;
}

.regulations-review-section__resolver {
    flex: 0 0 auto;
    align-self: center;
}

.regulations-review-section__unassigned {
    display: grid;
    gap: 1rem;
}

.regulations-review-section__unassigned-row {
    display: grid;
    grid-template-columns: minmax(12rem, .4fr) minmax(0, 1fr);
    gap: .75rem;
    align-items: end;
}

@media (max-width: 767.98px) {
    .regulations-review-section__heading,
    .regulations-review-section__unassigned-row {
        grid-template-columns: 1fr;
        flex-direction: column;
    }

    .regulations-review-section__actions {
        width: 100%;
        flex-wrap: wrap;
        margin-left: 0;
    }
}
</style>
